import type { CrmContact, DispatchIdentityRole } from '../../api/types';
import { primaryIdentity, roleLabel } from './dispatchIdentity';

/**
 * Compact colour-coded badge naming which kind of dispatch account a number
 * belongs to. One hue per role (tokens in comms.css) so the kind reads before
 * the label does, matching dispatch-dashboard's SMS inbox chip.
 */
export function DispatchRoleChip({
  role,
  className = 'cv-row-role',
}: {
  role: DispatchIdentityRole;
  className?: string;
}) {
  return (
    <span
      className={className}
      style={{ background: `var(--c-role-${role}-muted)`, color: `var(--c-role-${role})` }}
      title={`Matched to a dispatch ${roleLabel(role).toLowerCase()} by phone number`}
    >
      {roleLabel(role)}
    </span>
  );
}

/**
 * Who a contact is to dispatch, in one chip, on a conversation row of either
 * layout. A contact linked to a dispatch customer — the exact email or phone
 * match the bot's booking access rests on — says Customer; otherwise
 * dispatch's last-ten-digit phone match names the role (a lead, not a link,
 * which is why it keeps DispatchRoleChip's own title). A stranger gets none,
 * and that absence is the information.
 */
export function ContactRoleChip({ contact }: { contact: CrmContact }) {
  if (contact.dispatchCustomerId) {
    return (
      <span
        className="cv-row-role"
        style={{ background: 'var(--c-role-customer-muted)', color: 'var(--c-role-customer)' }}
        title={`Dispatch customer #${contact.dispatchCustomerId}${contact.dispatchLinkVerified ? ' · verified' : ''}`}
      >
        Customer
      </span>
    );
  }
  const identity = primaryIdentity(contact);
  return identity ? <DispatchRoleChip role={identity.role} /> : null;
}
